import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import {
  Container, Typography, Box, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogActions, DialogContent,
  DialogTitle, TextField, FormControl, InputLabel, Select, MenuItem, Switch,
  FormControlLabel, Grid, Chip, Snackbar, Alert, CircularProgress, Tabs, Tab
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

function SimulationJobs() {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [userRoles, setUserRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [submittedJobs, setSubmittedJobs] = useState([]);
  const [expressions, setExpressions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [currentJob, setCurrentJob] = useState({
    llmModel: 'gpt-4',
    expressionId: '',
    transferScale: 'scale',
    sampling: 1,
    includeBackwards: false,
    status: 'draft'
  });
  const [isEditing, setIsEditing] = useState(false);

  const fetchJobs = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${API_URL}/api/simulations/jobs`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      // Separate jobs based on status
      const draftJobs = response.data.filter(job => job.status === 'draft');
      const otherJobs = response.data.filter(job => job.status !== 'draft');

      setJobs(draftJobs);
      setSubmittedJobs(otherJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch simulation jobs',
        severity: 'error'
      });
    }
  };
  
  // Fetch user roles and jobs
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${API_URL}/api/user`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setUserRoles(response.data.role || []);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setSnackbar({
          open: true,
          message: 'Failed to fetch user data',
          severity: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    const fetchExpressions = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${API_URL}/api/simulations/expression`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        console.log('Fetched expressions:', response.data);
        setExpressions(response.data);
      } catch (error) {
        console.error('Error fetching expressions:', error);
        setSnackbar({
          open: true,
          message: 'Failed to fetch expressions',
          severity: 'error'
        });
      }
    };

    const fetchFirstJobs = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${API_URL}/api/simulations/jobs`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        // Separate jobs based on status
        const draftJobs = response.data.filter(job => job.status === 'draft');
        const otherJobs = response.data.filter(job => job.status !== 'draft');

        setJobs(draftJobs);
        setSubmittedJobs(otherJobs);
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setSnackbar({
          open: true,
          message: 'Failed to fetch simulation jobs',
          severity: 'error'
        });
      }
    };
    if (isAuthenticated) {
      fetchUserData();
      fetchExpressions();
      fetchFirstJobs();
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  const handleCreateJob = () => {
    setCurrentJob({
      llmModel: 'gpt-4',
      expressionId: expressions.length > 0 ? expressions[0]._id : '',
      transferScale: 'scale',
      sampling: 1,
      includeBackwards: false,
      status: 'draft'
    });
    setIsEditing(false);
    setOpenDialog(true);
  };

  const handleEditJob = (job) => {
    setCurrentJob({ ...job });
    setIsEditing(true);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setCurrentJob({
      ...currentJob,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const calculateCostEstimate = () => {
    // Simple cost estimation logic
    const modelCostFactor = currentJob.llmModel === 'gpt-4' ? 0.05 : 0.01;
    const scaleFactor = currentJob.transferScale === 'scale' ? 5 : 1;
    const backwardsFactor = currentJob.includeBackwards ? 2 : 1;
    return (modelCostFactor * scaleFactor * backwardsFactor * currentJob.sampling).toFixed(2);
  };

  const saveJob = async () => {
    try {
      const token = await getAccessTokenSilently();
      const method = isEditing ? 'put' : 'post';
      const url = isEditing
        ? `${API_URL}/api/simulations/jobs/${currentJob._id}`
        : `${API_URL}/api/simulations/jobs`;

      const response = await axios({
        method,
        url,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        data: {
          ...currentJob,
          costEstimate: calculateCostEstimate()
        }
      });

      console.log('Job saved:', response.data);

      setSnackbar({
        open: true,
        message: isEditing ? 'Job updated successfully' : 'New job created',
        severity: 'success'
      });

      fetchJobs();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving job:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to save job',
        severity: 'error'
      });
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      const token = await getAccessTokenSilently();
      await axios.delete(`${API_URL}/api/simulations/jobs/${jobId}`, {

        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSnackbar({
        open: true,
        message: 'Job deleted successfully',
        severity: 'success'
      });

      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete job',
        severity: 'error'
      });
    }
  };

  const submitJob = async (jobId) => {
    try {
      const token = await getAccessTokenSilently();
      await axios.put(`${API_URL}/api/simulations/jobs/${jobId}/submit`, {}, {

        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSnackbar({
        open: true,
        message: 'Job submitted for review',
        severity: 'success'
      });

      fetchJobs();
    } catch (error) {
      console.error('Error submitting job:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to submit job',
        severity: 'error'
      });
    }
  };

  const reviewJob = async (jobId, approved) => {
    try {
      const token = await getAccessTokenSilently();

      await axios.put(`${API_URL}/api/simulations/jobs/${jobId}/review`,

        { approved },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSnackbar({
        open: true,
        message: approved ? 'Job approved' : 'Job rejected',
        severity: 'success'
      });

      fetchJobs();
    } catch (error) {
      console.error('Error reviewing job:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to review job',
        severity: 'error'
      });
    }
  };

  const withdrawJob = async (jobId) => {
    try {
      const token = await getAccessTokenSilently();

      await axios.put(`${API_URL}/api/simulations/jobs/${jobId}/withdraw`, {}, {

        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSnackbar({
        open: true,
        message: 'Job withdrawn successfully',
        severity: 'success'
      });

      fetchJobs();
    } catch (error) {
      console.error('Error withdrawing job:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to withdraw job',
        severity: 'error'
      });
    }
  };

  const formatExpressionName = (expression) => {
    if (!expression) return 'Unknown';

    return expression.expression.expression // wtf is this bro... How can we have an expression inside an expression inside an expression?
      .map(op => (op.name.split(" ").map(word => word[0])).join("."))
      .join(" + ");
  };

  const getStatusChip = (status) => {
    const statusColors = {
      draft: 'default',
      submitted: 'info',
      approved: 'success',
      rejected: 'error',
      completed: 'secondary'
    };
    
    return (
      <Chip
        label={status.toUpperCase()}
        color={statusColors[status] || 'default'}
        size="small"

      />
    );
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const isAdmin = userRoles.includes('admin');

  if (isLoading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Simulation Jobs
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Draft Jobs" />
          <Tab label="Submitted Jobs" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"

              onClick={handleCreateJob}
            >
              Create New Job
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>LLM Model</TableCell>
                  <TableCell>Expression</TableCell>
                  <TableCell>Transfer Scale</TableCell>
                  <TableCell>Sampling</TableCell>
                  <TableCell>Bidirectional</TableCell>
                  <TableCell>Cost Estimate</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No draft jobs found. Create a new job to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  jobs.map((job) => (
                    <TableRow key={job._id}>
                      <TableCell>{job.llmModel}</TableCell>
                      <TableCell>
                        {expressions.find(e => e._id === job.expressionId)

                          ? formatExpressionName(expressions.find(e => e._id === job.expressionId).expression)
                          : 'Unknown'}
                      </TableCell>
                      <TableCell>{job.transferScale}</TableCell>
                      <TableCell>{job.sampling}</TableCell>
                      <TableCell>{job.includeBackwards ? 'Yes' : 'No'}</TableCell>
                      <TableCell>${job.costEstimate}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => handleEditJob(job)}
                          sx={{ mr: 1 }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => deleteJob(job._id)}
                          sx={{ mr: 1 }}
                        >
                          Delete
                        </Button>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => submitJob(job._id)}
                        >
                          Submit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>LLM Model</TableCell>
                <TableCell>Expression</TableCell>
                <TableCell>Transfer Scale</TableCell>
                <TableCell>Sampling</TableCell>
                <TableCell>Bidirectional</TableCell>
                <TableCell>Cost Estimate</TableCell>
                <TableCell>Status</TableCell>
                {isAdmin && <TableCell>Author</TableCell>}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submittedJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 9 : 8} align="center">
                    No submitted jobs found.
                  </TableCell>
                </TableRow>
              ) : (
                submittedJobs.map((job) => (
                  <TableRow key={job._id}>
                    <TableCell>{job.llmModel}</TableCell>
                    <TableCell>
                      {expressions.find(e => e._id === job.expressionId)
                        ? formatExpressionName(expressions.find(e => e._id === job.expressionId).expression)
                        : 'Unknown'}
                    </TableCell>
                    <TableCell>{job.transferScale}</TableCell>
                    <TableCell>{job.sampling}</TableCell>
                    <TableCell>{job.includeBackwards ? 'Yes' : 'No'}</TableCell>
                    <TableCell>${job.costEstimate}</TableCell>
                    <TableCell>{getStatusChip(job.status)}</TableCell>
                    {isAdmin && <TableCell>{job.auth0ID === user.sub ? 'You' : job.auth0ID}</TableCell>}
                    <TableCell>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {job.status === 'submitted' && job.auth0ID === user.sub && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => withdrawJob(job._id)}
                            sx={{ mr: 1 }}
                          >
                            WITHDRAW
                          </Button>
                        )}
                        {isAdmin && job.status === 'submitted' && (
                          <>
                            <Button
                              size="small"
                              color="success"
                              variant="contained"
                              onClick={() => reviewJob(job._id, true)}
                              sx={{ mr: 1 }}
                            >
                              APPROVE
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="contained"
                              onClick={() => reviewJob(job._id, false)}
                            >
                              REJECT
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Job Creation/Editing Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Simulation Job' : 'Create New Simulation Job'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>LLM Model</InputLabel>
                <Select
                  name="llmModel"
                  value={currentJob.llmModel}
                  onChange={handleInputChange}
                  label="LLM Model"
                >
                  <MenuItem value="gpt-4">GPT-4</MenuItem>
                  <MenuItem value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>
                  <MenuItem value="claude-2">Claude 2</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Base Expression</InputLabel>
                <Select
                  name="expressionId"
                  value={currentJob.expressionId}
                  onChange={handleInputChange}
                  label="Base Expression"
                >
                  {expressions.map(expr => (
                    <MenuItem key={expr._id} value={expr._id}>
                      {formatExpressionName(expr)} - {new Date(expr.date_created).toLocaleDateString()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Transfer Scale</InputLabel>
                <Select
                  name="transferScale"
                  value={currentJob.transferScale}
                  onChange={handleInputChange}
                  label="Transfer Scale"
                >
                  <MenuItem value="scale">Full Scale</MenuItem>
                  <MenuItem value="test">Test Run</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                margin="normal"
                label="Sampling"
                name="sampling"
                type="number"
                value={currentJob.sampling}
                onChange={handleInputChange}
                InputProps={{ inputProps: { min: 1, max: 100 } }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentJob.includeBackwards}
                    onChange={handleInputChange}
                    name="includeBackwards"
                  />
                }
                label="Include Backwards Analysis"
              />
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Cost Estimate: ${calculateCostEstimate()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This is an estimated cost based on your current settings.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={saveJob} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>

          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default SimulationJobs;
